import type { BetriebStatusResponse } from "../api/types";

export interface SectionTab {
  label: string;
  to: string;
  /** Title of the detail page below this tab (e.g. /anrufe/:id). */
  detail?: string;
}

/**
 * main: Kopfzeile und untere Leiste. experte: unter "Mehr".
 * ruht: "Eigene Sprachstrecke (ruht)", eingeklappt unter "Mehr"; diese
 * Seiten zeigen den Ruhehinweis und fragen nichts im Takt ab.
 */
export type SectionGroup = "main" | "experte" | "ruht";

export interface Section {
  label: string;
  /** Landing route, equals the first tab when tabs exist. */
  to: string;
  group: SectionGroup;
  /** Letter after "g" that jumps here (including sections under Mehr). */
  key?: string;
  tabs: SectionTab[];
  /** Further route prefixes owned by this section (legacy, aliases, details). */
  also?: string[];
  /** Detail title for routes under `also` (e.g. /rueckruf/:id). */
  detail?: string;
  count?: (status: BetriebStatusResponse | null) => number | null;
}

/**
 * Informationsarchitektur Leitstand Stufe 1 (11.10.2026): fünf Ziele für
 * den Alltag, Experte und die ruhende Sprachstrecke unter "Mehr". Einzige
 * Quelle für Kopfzeile, untere Leiste, Tabs, Seitentitel, Pfad, Sprungtasten
 * und die Hilfeseite.
 */
export const SECTIONS: Section[] = [
  { label: "Heute", to: "/", group: "main", key: "h", tabs: [] },
  {
    label: "Gespräche", to: "/zusammenfassungen", group: "main", key: "s",
    tabs: [], detail: "Gespräch",
  },
  {
    label: "Rückrufe", to: "/rueckrufe", group: "main", key: "r", tabs: [],
    also: ["/rueckruf", "/warteschlange"], detail: "Rückruf",
    count: (status) => status?.rueckrufe?.offen ?? null,
  },
  {
    label: "Auswertung", to: "/auswertung", group: "main", key: "a",
    tabs: [], detail: "Auswertungslauf",
  },
  {
    label: "Betrieb",
    to: "/betrieb",
    group: "main",
    key: "b",
    tabs: [
      { label: "Status", to: "/betrieb" },
      { label: "Anrufregeln", to: "/betrieb/anrufregeln" },
      { label: "Verbindungen", to: "/verbindungen" },
    ],
  },
  {
    label: "Lernen", to: "/wissensluecken", group: "experte", key: "l",
    tabs: [
      { label: "Wissenslücken", to: "/wissensluecken", detail: "Wissenslücke" },
      { label: "Wissensfreigaben", to: "/wissensfreigaben" },
    ],
  },
  {
    label: "Experte",
    to: "/wissensbasis",
    group: "experte",
    key: "x",
    tabs: [
      { label: "Wissensbasis", to: "/wissensbasis" },
      { label: "PIM", to: "/pim" },
      { label: "Richtlinien", to: "/richtlinien" },
      { label: "Maschinenzugänge", to: "/einstellungen/maschinenzugaenge" },
      { label: "Audit-Log", to: "/compliance" },
      { label: "Einstellungen", to: "/einstellungen" },
    ],
  },
  {
    label: "Eigene Sprachstrecke",
    to: "/live",
    group: "ruht",
    tabs: [
      { label: "Live", to: "/live" },
      { label: "Anrufe", to: "/anrufe", detail: "Anruf-Detail" },
      { label: "Fälle", to: "/faelle", detail: "Fall-Detail" },
      { label: "Qualität", to: "/qualitaet" },
      { label: "Themen", to: "/themen" },
      { label: "Agent", to: "/agent" },
      { label: "Testkonsole", to: "/testkonsole" },
      { label: "Tests", to: "/tests" },
      { label: "Workflows", to: "/workflows" },
      { label: "Einrichtung", to: "/einrichtung" },
      { label: "Analytik", to: "/analytik" },
      { label: "Frühere Startseite", to: "/uebersicht" },
      { label: "Demo", to: "/demo" },
    ],
    also: ["/anruf-detail"],
  },
];

export const MAIN_SECTIONS = SECTIONS.filter((s) => s.group === "main");

/** Routes outside the sections (reachable via "Mehr" or "?"). */
export const STANDALONE_PAGES: { to: string; label: string }[] = [
  { to: "/hilfe", label: "Hilfe" },
];

/** "g" followed by a letter jumps to a section (Inbox pattern, see research). */
export const GOTO_KEYS: Record<string, string> = Object.fromEntries(
  SECTIONS.filter((s) => s.key).map((s) => [s.key as string, s.to]),
);

function matches(prefix: string, pathname: string): boolean {
  if (prefix === "/") return pathname === "/";
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export interface Placement {
  section: Section;
  /** Longest matching tab, undefined for sections without tabs. */
  tab?: SectionTab;
  /** True on a route below a tab or section landing (detail page). */
  detail: boolean;
}

/** Where a pathname lives. Null for standalone pages and unknown routes. */
export function placementFor(pathname: string): Placement | null {
  let best: Placement | null = null;
  let bestLen = -1;
  for (const section of SECTIONS) {
    // Tabs first so a tab wins the tie against the section landing route.
    const prefixes: { prefix: string; tab?: SectionTab }[] = [
      ...section.tabs.map((tab) => ({ prefix: tab.to, tab })),
      { prefix: section.to },
      ...(section.also ?? []).map((prefix) => ({ prefix })),
    ];
    for (const { prefix, tab } of prefixes) {
      if (matches(prefix, pathname) && prefix.length > bestLen) {
        bestLen = prefix.length;
        best = { section, tab, detail: pathname !== prefix };
      }
    }
  }
  return best;
}

/** True for routes of the resting own voice line. */
export function isRuhend(pathname: string): boolean {
  return placementFor(pathname)?.section.group === "ruht";
}

/** Breadcrumb Bereich > Tab > Detail; empty above depth 2. */
export function breadcrumbFor(pathname: string): { label: string; to?: string }[] {
  const place = placementFor(pathname);
  if (!place || !place.detail) return [];
  return [
    { label: place.section.label, to: place.section.to },
    ...(place.tab ? [{ label: place.tab.label, to: place.tab.to }] : []),
    { label: place.tab?.detail ?? place.section.detail ?? "Detail" },
  ];
}

/** Document title, e.g. "Wissenslücken · Lernen" or "Anruf-Detail". */
export function pageTitle(pathname: string): string {
  const standalone = STANDALONE_PAGES.find((p) => matches(p.to, pathname));
  if (standalone) return standalone.label;
  const place = placementFor(pathname);
  if (!place) return "Heute";
  if (place.detail) return place.tab?.detail ?? place.section.detail ?? `${place.section.label}: Detail`;
  if (!place.tab) return place.section.label;
  return `${place.tab.label} · ${place.section.label}`;
}
